import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { money, today, yearStart, ugx } from "@/lib/format";
import { PageHeader, Panel, Empty } from "@/components/Shell";

const TABS = [
  ["profit-loss", "Profit & Loss"],
  ["balance-sheet", "Balance Sheet"],
  ["trial-balance", "Trial Balance"],
  ["vat-return", "VAT Return"],
  ["efris", "EFRIS Filing"],
  ["ar-aging", "A/R Aging"],
  ["ap-aging", "A/P Aging"],
  ["general-ledger", "General Ledger"],
];

const SALES_COLS = [
  { key: "invoice_date", label: "Invoice Date" },
  { key: "invoice_no", label: "Invoice No" },
  { key: "buyer_name", label: "Buyer Name" },
  { key: "buyer_tin", label: "Buyer TIN" },
  { key: "invoice_type", label: "Invoice Type" },
  { key: "taxable_amount", label: "Taxable Amount (UGX)" },
  { key: "vat_rate", label: "VAT Rate %" },
  { key: "vat_amount", label: "VAT Amount (UGX)" },
  { key: "wht_amount", label: "WHT Amount (UGX)" },
  { key: "gross_amount", label: "Gross Amount (UGX)" },
  { key: "currency", label: "Currency" },
];

const PURCHASE_COLS = [
  { key: "invoice_date", label: "Invoice Date" },
  { key: "invoice_no", label: "Invoice No" },
  { key: "supplier_name", label: "Supplier Name" },
  { key: "supplier_tin", label: "Supplier TIN" },
  { key: "source", label: "Document Type" },
  { key: "taxable_amount", label: "Taxable Amount (UGX)" },
  { key: "vat_rate", label: "VAT Rate %" },
  { key: "vat_amount", label: "Input VAT (UGX)" },
  { key: "wht_amount", label: "WHT Withheld (UGX)" },
  { key: "gross_amount", label: "Gross Amount (UGX)" },
];

const Row = ({ label, value, bold, indent }) => (
  <div className={`flex items-center justify-between border-b border-slate-100 px-5 py-2 last:border-0 ${bold ? "font-display font-bold text-slate-900" : "text-slate-700"}`}>
    <span className={indent ? "pl-4" : ""}>{label}</span>
    <span className="tabular-nums">{money(value)}</span>
  </div>
);

export default function Reports() {
  const [tab, setTab] = useState("profit-loss");
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    const params = tab === "balance-sheet" ? { end } : { start, end };
    const path = tab === "efris" ? "/efris/vat-return" : `/reports/${tab}`;
    api.get(path, { params }).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [tab, start, end]);

  return (
    <div data-testid="reports-page">
      <PageHeader title="Financial Reports" subtitle="URA-ready statements generated live from your double-entry ledger." />

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-2">
          {TABS.map(([k, label]) => (
            <button key={k} data-testid={`report-tab-${k}`} onClick={() => { setData(null); setTab(k); }}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                tab === k ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">From</label>
            <input data-testid="report-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">To</label>
            <input data-testid="report-end-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" />
          </div>
        </div>
      </div>

      {!data ? <p className="text-sm text-slate-500">Preparing report…</p> : (
        <div data-testid="report-content">
          {tab === "profit-loss" && (
            <Panel title="Profit & Loss (UGX)">
              <p className="px-5 pt-4 text-xs font-semibold uppercase tracking-widest text-slate-400">Income</p>
              {data.income.length ? data.income.map((r) => <Row key={r.code} label={`${r.code} · ${r.name}`} value={r.amount} indent />) : <Empty message="No income recorded." />}
              <Row label="Total income" value={data.total_income} bold />
              <p className="px-5 pt-4 text-xs font-semibold uppercase tracking-widest text-slate-400">Cost of sales</p>
              {data.cogs.map((r) => <Row key={r.code} label={`${r.code} · ${r.name}`} value={r.amount} indent />)}
              <Row label="Gross profit" value={data.gross_profit} bold />
              <p className="px-5 pt-4 text-xs font-semibold uppercase tracking-widest text-slate-400">Operating expenses</p>
              {data.expenses.map((r) => <Row key={r.code} label={`${r.code} · ${r.name}`} value={r.amount} indent />)}
              <Row label="Total expenses" value={data.total_expenses} bold />
              <div className="flex items-center justify-between bg-slate-900 px-5 py-3 font-display text-base font-bold text-white">
                <span>Net profit</span><span className="tabular-nums" data-testid="pl-net-profit">{ugx(data.net_profit)}</span>
              </div>
            </Panel>
          )}

          {tab === "balance-sheet" && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Panel title="Assets">
                {data.assets.map((r) => <Row key={r.code} label={`${r.code} · ${r.name}`} value={r.amount} />)}
                <Row label="Total assets" value={data.total_assets} bold />
              </Panel>
              <Panel title="Liabilities & Equity">
                {data.liabilities.map((r) => <Row key={r.code} label={`${r.code} · ${r.name}`} value={r.amount} />)}
                <Row label="Total liabilities" value={data.total_liabilities} bold />
                {data.equity.map((r) => <Row key={r.code} label={`${r.code} · ${r.name}`} value={r.amount} />)}
                <Row label="Current year earnings" value={data.current_earnings} />
                <Row label="Total equity" value={data.total_equity} bold />
                <div className="flex items-center justify-between bg-slate-900 px-5 py-3 font-display text-sm font-bold text-white">
                  <span>Liabilities + Equity</span>
                  <span className="tabular-nums" data-testid="bs-total">{ugx(data.total_liabilities_equity)}</span>
                </div>
                <p className={`px-5 py-2 text-xs font-semibold ${data.balanced ? "text-emerald-700" : "text-rose-600"}`} data-testid="bs-balanced">
                  {data.balanced ? "Balance sheet is balanced" : "Warning: balance sheet does not balance"}
                </p>
              </Panel>
            </div>
          )}

          {tab === "trial-balance" && (
            <Panel title="Trial Balance">
              <table className="w-full text-sm" data-testid="trial-balance-table">
                <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="px-5 py-2.5">Code</th><th className="px-5 py-2.5">Account</th>
                    <th className="px-5 py-2.5 text-right">Debit</th><th className="px-5 py-2.5 text-right">Credit</th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.code} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-2 font-mono text-xs">{r.code}</td>
                      <td className="px-5 py-2 text-slate-800">{r.name}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{r.debit ? money(r.debit) : "—"}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{r.credit ? money(r.credit) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-900 font-display font-bold text-white">
                    <td className="px-5 py-3" colSpan={2}>Totals</td>
                    <td className="px-5 py-3 text-right tabular-nums" data-testid="tb-total-debit">{money(data.total_debit)}</td>
                    <td className="px-5 py-3 text-right tabular-nums" data-testid="tb-total-credit">{money(data.total_credit)}</td>
                  </tr>
                </tfoot>
              </table>
            </Panel>
          )}

          {tab === "vat-return" && (
            <Panel title="VAT & Withholding Tax Return (URA)">
              <Row label="Taxable sales (excl. VAT)" value={data.taxable_sales} />
              <Row label="Output VAT collected (18%)" value={data.output_vat} />
              <Row label="Taxable purchases & expenses" value={data.taxable_purchases} />
              <Row label="Input VAT claimable" value={data.input_vat} />
              <div className="flex items-center justify-between bg-slate-900 px-5 py-3 font-display text-base font-bold text-white">
                <span>Net VAT payable to URA</span>
                <span className="tabular-nums" data-testid="vat-net-payable">{ugx(data.net_vat_payable)}</span>
              </div>
              <Row label="Withholding tax recoverable (deducted by customers)" value={data.wht_recoverable} />
              <Row label="Withholding tax payable to URA (deducted from suppliers)" value={data.wht_payable} />
            </Panel>
          )}

          {tab === "efris" && (
            <div className="space-y-6">
              <Panel title="URA VAT Return (EFRIS format)"
                right={
                  <div className="flex gap-2">
                    <button data-testid="download-sales-schedule-button"
                      onClick={() => downloadCsv(`EFRIS_Sales_Schedule_${start}_${end}.csv`, SALES_COLS, data.sales_schedule)}
                      className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      <Download size={13} /> Sales schedule CSV
                    </button>
                    <button data-testid="download-purchase-schedule-button"
                      onClick={() => downloadCsv(`EFRIS_Purchase_Schedule_${start}_${end}.csv`, PURCHASE_COLS, data.purchase_schedule)}
                      className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      <Download size={13} /> Purchase schedule CSV
                    </button>
                  </div>
                }>
                <div className="grid grid-cols-1 gap-4 border-b border-slate-200 bg-slate-50 p-5 sm:grid-cols-4">
                  {[["Taxpayer", data.taxpayer.name], ["TIN", data.taxpayer.tin || "—"],
                    ["VAT No.", data.taxpayer.vat_number || "—"],
                    ["Return period", `${data.period.from || "—"} → ${data.period.to || "—"}`]].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-xs uppercase tracking-widest text-slate-500">{k}</p>
                      <p className="text-sm font-semibold text-slate-900">{v}</p>
                    </div>
                  ))}
                </div>
                <table className="w-full text-sm" data-testid="efris-boxes-table">
                  <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="px-5 py-2.5 w-16">Box</th><th className="px-5 py-2.5">Description</th>
                      <th className="px-5 py-2.5 text-right">Amount (UGX)</th></tr>
                  </thead>
                  <tbody>
                    {data.boxes.map((b) => (
                      <tr key={b.box} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-5 py-2.5 font-mono text-xs font-semibold text-slate-500">{b.box}</td>
                        <td className="px-5 py-2.5 text-slate-800">{b.label}</td>
                        <td className="px-5 py-2.5 text-right tabular-nums font-medium">{money(b.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between bg-slate-900 px-5 py-3 font-display text-base font-bold text-white">
                  <span>Net VAT payable to URA</span>
                  <span className="tabular-nums" data-testid="efris-net-vat">{ugx(data.totals.net_vat_payable)}</span>
                </div>
              </Panel>

              <Panel title={`Sales schedule · ${data.sales_schedule.length} invoices`}>
                {data.sales_schedule.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="efris-sales-table">
                      <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                        <tr>{SALES_COLS.map((c) => <th key={c.key} className="px-4 py-2.5 whitespace-nowrap">{c.label}</th>)}</tr>
                      </thead>
                      <tbody>
                        {data.sales_schedule.map((r, i) => (
                          <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                            {SALES_COLS.map((c) => (
                              <td key={c.key} className={`px-4 py-2 whitespace-nowrap ${typeof r[c.key] === "number" ? "text-right tabular-nums" : "text-slate-700"}`}>
                                {typeof r[c.key] === "number" ? money(r[c.key]) : r[c.key] || "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <Empty message="No sales invoices in this period." />}
              </Panel>

              <Panel title={`Purchase schedule · ${data.purchase_schedule.length} documents`}>
                {data.purchase_schedule.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="efris-purchase-table">
                      <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                        <tr>{PURCHASE_COLS.map((c) => <th key={c.key} className="px-4 py-2.5 whitespace-nowrap">{c.label}</th>)}</tr>
                      </thead>
                      <tbody>
                        {data.purchase_schedule.map((r, i) => (
                          <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                            {PURCHASE_COLS.map((c) => (
                              <td key={c.key} className={`px-4 py-2 whitespace-nowrap ${typeof r[c.key] === "number" ? "text-right tabular-nums" : "text-slate-700"}`}>
                                {typeof r[c.key] === "number" ? money(r[c.key]) : r[c.key] || "—"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <Empty message="No purchases or expenses in this period." />}
              </Panel>
            </div>
          )}

          {(tab === "ar-aging" || tab === "ap-aging") && (
            <Panel title={tab === "ar-aging" ? "Accounts Receivable Aging" : "Accounts Payable Aging"}>
              {data.rows.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="aging-table">
                    <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                      <tr><th className="px-5 py-2.5">{tab === "ar-aging" ? "Customer" : "Vendor"}</th>
                        <th className="px-5 py-2.5 text-right">Current</th><th className="px-5 py-2.5 text-right">1–30</th>
                        <th className="px-5 py-2.5 text-right">31–60</th><th className="px-5 py-2.5 text-right">61–90</th>
                        <th className="px-5 py-2.5 text-right">90+</th><th className="px-5 py-2.5 text-right">Total</th></tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <tr key={r.name} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-5 py-2 font-medium text-slate-800">{r.name}</td>
                          {["current", "1_30", "31_60", "61_90", "over_90", "total"].map((k) => (
                            <td key={k} className={`px-5 py-2 text-right tabular-nums ${k === "total" ? "font-semibold" : "text-slate-600"}`}>{money(r[k])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-900 font-display font-bold text-white">
                        <td className="px-5 py-3">Totals</td>
                        {["current", "1_30", "31_60", "61_90", "over_90", "total"].map((k) => (
                          <td key={k} className="px-5 py-3 text-right tabular-nums">{money(data.totals[k])}</td>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : <Empty message="Nothing outstanding." />}
            </Panel>
          )}

          {tab === "general-ledger" && (
            <Panel title="General Ledger">
              {data.rows.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ledger-table">
                    <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                      <tr><th className="px-5 py-2.5">Date</th><th className="px-5 py-2.5">Account</th>
                        <th className="px-5 py-2.5">Description</th><th className="px-5 py-2.5">Source</th>
                        <th className="px-5 py-2.5 text-right">Debit</th><th className="px-5 py-2.5 text-right">Credit</th></tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-5 py-2 text-slate-600">{r.date}</td>
                          <td className="px-5 py-2 font-medium text-slate-800">{r.account}</td>
                          <td className="px-5 py-2 text-slate-600">{r.description}</td>
                          <td className="px-5 py-2 capitalize text-slate-500">{r.source}</td>
                          <td className="px-5 py-2 text-right tabular-nums">{r.debit ? money(r.debit) : "—"}</td>
                          <td className="px-5 py-2 text-right tabular-nums">{r.credit ? money(r.credit) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <Empty message="No ledger activity in this period." />}
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}
